// src/components/roles/RoleBlockModal/RoleBlockModal.tsx
import React, { useState } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import { Block as BlockIcon, Info as InfoIcon } from '@mui/icons-material';
import { Role } from '../../../types/role.types';
import { ConfirmationModal } from '../../ui/Modal';
import { colors } from '../../../styles/theme/colors';

export interface RoleBlockModalProps {
  open: boolean;
  role: Role | null;
  onClose: () => void;
  onConfirm: (reason?: string) => Promise<void>;
  loading?: boolean;
}

const RoleBlockModal: React.FC<RoleBlockModalProps> = ({
  open,
  role,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [isBlocking, setIsBlocking] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  const handleClose = () => {
    if (!isBlocking) {
      setBlockReason('');
      onClose();
    }
  };

  const handleConfirm = async () => {
    setIsBlocking(true);
    try {
      await onConfirm(blockReason || undefined);
      setBlockReason('');
      onClose();
    } catch (error) {
      console.error('Error blocking role:', error);
    } finally {
      setIsBlocking(false);
    }
  };

  if (!role) return null;

  const customContent = (
    <Box
      sx={{
        mt: 2,
        textAlign: 'left',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          backgroundColor: 'rgba(16, 185, 129, 0.06)',
          borderRadius: '8px',
          padding: '10px 14px',
          mb: 2,
        }}
      >
        <InfoIcon sx={{ fontSize: 18, color: '#10b981', mt: '2px', flexShrink: 0 }} />
        <Box>
          <Typography variant="body2" sx={{ color: '#10b981', mb: 1 }}>
            Impacto del bloqueo:
          </Typography>
          <Typography variant="body2" component="div" sx={{ color: '#10b981', lineHeight: 1.5 }}>
            • {role.userCount} usuario(s) tienen este rol asignado
          </Typography>
          <Typography variant="body2" component="div" sx={{ color: '#10b981', lineHeight: 1.5 }}>
            • Estos usuarios mantendrán el rol y sus permisos
          </Typography>
          <Typography variant="body2" component="div" sx={{ color: '#10b981', lineHeight: 1.5 }}>
            • No se podrá asignar este rol a nuevos usuarios
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          p: 2.5,
          borderRadius: '8px',
          backgroundColor: colors.grey[50],
          border: `1px solid ${colors.grey[200]}`,
          mb: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 500,
            display: 'block',
            mb: 1,
          }}
        >
          Rol
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: colors.text.primary,
            mb: 1.5,
          }}
        >
          {role.name}
        </Typography>
        {role.description && (
          <Typography
            variant="caption"
            sx={{
              color: colors.text.secondary,
              fontSize: '0.8125rem',
            }}
          >
            {role.description}
          </Typography>
        )}
      </Box>

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Razón del bloqueo (opcional)"
        value={blockReason}
        onChange={(e) => setBlockReason(e.target.value)}
        placeholder="Ej: Mantenimiento, revisión de permisos, etc."
        sx={{ mt: 2 }}
        disabled={isBlocking || loading}
      />
    </Box>
  );

  return (
    <ConfirmationModal
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={`Bloquear Rol: ${role.name}`}
      message="¿Desea bloquear este rol?"
      confirmText={isBlocking ? 'Bloqueando...' : 'Confirmar Bloqueo'}
      cancelText="Cancelar"
      variant="warning"
      icon={<BlockIcon />}
      loading={isBlocking || loading}
      requireCheckbox={true}
      checkboxLabel="Entiendo el impacto del bloqueo"
      customContent={customContent}
    />
  );
};

export default RoleBlockModal;
